import React, { useRef, useState } from 'react'
import axios from 'axios'

import Link from '@atoms/Link'
import CrossMark from '@icons/CrossMark'
import { useTranslation } from '@models/Translation'

import * as style from './style.module.sass'

type RequestFormProps = {
  close: () => void
}

const RequestForm: React.FC<RequestFormProps> = ({ close }) => {
  const { navigate } = useTranslation()
  const ref = useRef<HTMLButtonElement>(null)
  const [isMessageValid, setIsMessageValid] = useState<boolean>()
  const [isNameValid, setIsNameValid] = useState<boolean>()
  const [isPrivacyChecked, setIsPrivacyChecked] = useState(false)

  const handleClick = () => {
    close()
  }

  const handleMessageBlur = (message: string) => {
    setIsMessageValid(message.length > 0 && message.length <= 400)
  }

  const handleNameBlur = (name: string) => {
    setIsNameValid(name.length > 0 && name.length <= 15)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    enableButton(false)
    e.preventDefault()
    const form = e.target as HTMLFormElement
    const formData = new FormData(form)

    axios
      .post('https://hooks.zapier.com/hooks/catch/5359107/3kvaa6f/', formData)
      .then(() => {
        close()
        navigate('/request/thanks')
      })
      .catch(() => {
        alert('送信に失敗しました。やり直してください。')
      })
      .finally(() => {
        enableButton(true)
      })
  }

  const enableButton = (enabled: boolean) => {
    if (ref.current) {
      ref.current.disabled = !enabled
    }
  }

  return (
    <div className={style.container} onClick={handleClick}>
      <div className={style.scrollContainer}>
        <div
          className={style.contents}
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <button className={style.close} onClick={handleClick}>
            <CrossMark />
          </button>
          <div className={style.title}>取材&記事リクエスト</div>
          <form className={style.form} onSubmit={handleSubmit}>
            <div className={style.item}>
              <label>
                <div className={style.label}>
                  取材してほしいことやこんな記事が読みたいなど、ご意見・ご要望をお聞かせください。
                  <span>（必須）</span>
                </div>
                <p className={style.description}>
                  ※すべてのリクエストにお応えはできませんが、その場合も必ず目を通し今後の記事作成に活かしてまいります。
                </p>
                <textarea
                  className={style.textarea}
                  name="message"
                  rows={5}
                  placeholder="ファッションテック領域で気になる人物、情報、ブランド、アイテムなど、400字以内で入力してください。"
                  required
                  onBlur={e => {
                    handleMessageBlur((e.target as HTMLTextAreaElement).value)
                  }}
                  data-valid={isMessageValid}
                />
              </label>
              <p className={style.alert} data-valid={isMessageValid}>
                リクエスト内容を入力してください（400文字以内）
              </p>
            </div>
            <div className={style.item}>
              <label>
                <div className={style.label}>
                  ペンネーム<span>（必須）</span>
                </div>
                <input
                  className={style.input}
                  type="text"
                  name="name"
                  placeholder="15文字以内で入力してください"
                  required
                  onBlur={e => {
                    handleNameBlur((e.target as HTMLInputElement).value)
                  }}
                  data-valid={isNameValid}
                />
              </label>
              <p className={style.alert} data-valid={isNameValid}>
                ペンネームを入力してください（15文字以内）
              </p>
            </div>
            <div className={style.item}>
              <label>
                <div className={style.label}>メールアドレス（任意）</div>
                <p className={style.description}>
                  ※取材にご協力いただける場合は、編集部からリクエストの詳細についてご連絡させていただく場合がございます。
                </p>
                <input className={style.input} type="email" name="email" />
              </label>
            </div>
            <p className={style.note}>
              【注意事項】
              <br />
              ・リクエストフォームから受領したテキストについては、内容の全部または一部を掲載及び内容を一部変更して掲載させていただく場合がございます。
              <br />
              ・リクエストフォームから第三者の権利を侵害する内容を送信する行為は、禁止いたします。
              <br />
              ・ペンネームは、リクエストを基に作成した記事内に掲載させていただく場合がございます。
              <br />
              ・リクエストを基に作成した記事に内包される知的財産権は、当社又は当社が記事作成を委託した第三者等に帰属するものとします。
            </p>
            <div className={style.privacy}>
              <label className={style.privacyLabel}>
                <div className={style.check} data-checked={isPrivacyChecked} />
                <input
                  type="checkbox"
                  required
                  onClick={e => {
                    setIsPrivacyChecked((e.target as HTMLInputElement).checked)
                  }}
                />
                <Link to="/privacy" target="_blank">
                  <span className={style.link}>プライバシーポリシー</span>
                </Link>
                に同意する
              </label>
            </div>
            <button
              className={style.submit}
              type="submit"
              disabled={!(isMessageValid && isNameValid && isPrivacyChecked)}
              ref={ref}
            >
              送信する
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default RequestForm
